import { useMutation } from '@zenbusiness/application-commons-graphql';
import { v4 as uuidv4 } from 'uuid';

import { CARD_TYPE, CREATE_CARD_ERROR } from '#client/constants/cards';
import { GENERATING_SENSITIVE_DATA } from '#client/constants/time';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useCardsContext from '#client/hooks/useCardsContext';
import useConst from '#client/hooks/useConst';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';

import { REQUEST_NEW_CARD_MUTATION } from '../mutations/card';

const useReplaceCard = () => {
  const { unitAccountId } = useUnitAccountId();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { getCustomerToken, loading: customerTokenLoading } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });
  const idempotencyKey = useConst(uuidv4);

  const [requestNewCard, { loading: requestNewCardLoading }] = useMutation(
    REQUEST_NEW_CARD_MUTATION,
    {
      noThrow: true
    }
  );
  const { refetchCard, setIsGeneratingSensitiveData } = useCardsContext();
  const { createSuccessToast, createErrorToast } = useToastContext();

  const handleReplaceCard = async ({
    cardId,
    businessAddress,
    personalAddress,
    selectedAddress,
    cardType,
    reason,
    handleClose
  }) => {
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      handleClose();
      return;
    }

    const { error: requestNewCardError } = await requestNewCard({
      variables: {
        input: {
          id: cardId,
          identifier: {
            businessUuid: businessEntityUuid,
            unitAccountId
          },
          attributes: {
            shippingAddress: selectedAddress === 'business' ? businessAddress : personalAddress,
            type: cardType,
            idempotencyKey
          },
          reason,
          customerToken
        }
      },
      skip: !unitAccountId
    });

    if (requestNewCardError) {
      createErrorToast({
        title: CREATE_CARD_ERROR.title,
        description: CREATE_CARD_ERROR.description
      });
      refetchCard();
      handleClose();

      return;
    }

    if (cardType === CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD) {
      setIsGeneratingSensitiveData(true);
      setTimeout(() => setIsGeneratingSensitiveData(false), GENERATING_SENSITIVE_DATA);
    }

    createSuccessToast({
      title:
        cardType === CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD
          ? 'Your new virtual card is ready to use'
          : 'Your new card is on the way',
      description:
        cardType === CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD
          ? ''
          : 'It’ll be there in 7 to 10 business days.'
    });

    refetchCard();
    handleClose();
  };

  return {
    loading: requestNewCardLoading || customerTokenLoading,
    handleReplaceCard
  };
};

export default useReplaceCard;
